"use client"

import React, { useState, useTransition } from "react"
import { useLocale, useTranslations } from "next-intl"
import { Check, Minus } from "@medusajs/icons"
import { toast } from "@medusajs/ui"
import { BorderBeamButton } from "@/components/ui/border-beam-button"
import { CreditCardIcon } from "@/components/ui/credit-card"
import { DownloadIcon } from "@/components/ui/download"
import { HandCoinsIcon } from "@/components/ui/hand-coins"

import { Link } from "@i18n/navigation"
import { usePathname, useRouter } from "@i18n/navigation"
import { startCheckout } from "@lib/data/checkout"
import { claimTrial } from "@lib/data/trial"
import type { ProPlans } from "@lib/data/pricing"
import type { HomeContentData } from "@lib/data/site-content"
import type { PurchaseGate } from "@lib/util/purchase-gate"
import { deriveTrialPhase, trialGateBlock } from "@lib/util/trial-phase"
import { useTrialClaim } from "@modules/saas/trial-claim/context"
import { DOWNLOAD_FREE_URL } from "@lib/util/env"
import { getPercentageDiff } from "@lib/util/get-percentage-diff"
import { formatAmount } from "@lib/util/money"
import { cn } from "@/lib/utils"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { GlareHover } from "@/components/ui/glare-hover"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

type PricingSectionCardsProps = {
  /** Live PRO prices from the Medusa Store API via the server parent. */
  plans: ProPlans
  /**
   * Purchase matrix (0.8.5 工单 08 / D9): when the customer already has a
   * subscription the engine cannot fold a new purchase into (native / bound
   * trial / paused / past_due), the subscribe CTA is disabled with the reason.
   */
  purchaseGate: PurchaseGate
  /**
   * 后台可配的卡片文案与对比表（2026-11-02，需求 11.4/11.5）。由服务端壳
   * （`@modules/landing/components/pricing`）读 `home-content` 后注入；值为空时
   * 数据层已回落到 message catalog，所以这里拿到的永远是成品字符串/数组。
   */
  content: HomeContentData
}

/**
 * Landing pricing section (pricing-sections cards block, adapted).
 *
 * Client component on purpose: the monthly/yearly Switch state selects the
 * billing cycle that is fed into `startCheckout({ cycle })` — the site's only
 * checkout entry point, migrated verbatim from the replaced plan-cards.
 * Prices arrive as realtime Medusa data (minor/major units per the backend's
 * big-number rule) via the `plans` prop; nothing here rescales them.
 */
export default function PricingSectionCards({
  plans,
  purchaseGate,
  content,
}: PricingSectionCardsProps) {
  const t = useTranslations("Pricing")
  // B0-2：自动扣费披露的文案只有一处（试用横幅的
  // TrialBanner.autoRenewDisclosure）。首页领取路径的成功通知复用它，
  // 免得同一句话在两个文件里各写一份、日后改一处漏一处。
  const tTrialBanner = useTranslations("TrialBanner")
  const locale = useLocale()
  const [yearly, setYearly] = useState(false)
  const [pending, startTransition] = useTransition()
  // 领取期间禁用按钮：双击不能发出两次 claim。
  const [claimBusy, setClaimBusy] = useState(false)
  const { open: openTrial, state, refresh } = useTrialClaim()
  const router = useRouter()
  const pathname = usePathname()

  const { monthly, yearly: yearlyPlan, currencyCode } = plans

  /** Major-unit formatting through the shared, division-free formatter. */
  const formatMinor = (amount: number, currency: string) =>
    formatAmount({ amount, currency_code: currency, locale })

  const cycle: "monthly" | "yearly" = yearly ? "yearly" : "monthly"
  const active = yearly ? yearlyPlan : monthly
  const price = active ?? monthly ?? yearlyPlan

  const savePercent =
    monthly && yearlyPlan && monthly.amount > 0
      ? Number(getPercentageDiff(monthly.amount * 12, yearlyPlan.amount))
      : 0

  // 卡片文案与对比表来自后台（`content`），价格仍来自 `plans`。
  const { cards, comparisonTable } = content
  const freeFeatures = cards.free.features
  const proFeatures = cards.pro.features
  const comparisonGroups = comparisonTable.groups

  const subscribe = () => {
    startTransition(() => {
      // Server action: redirects (anonymous → sign-in) or to
      // /checkout?cycle=…&currency=… . Currency decides the payment rail
      // downstream (CNY → cashier, others → PayPal subscription).
      //
      // 币种要显式带上（2026-10-31）：不带的话服务端只能按语言给默认值，
      // 而客人可能刚从结账页选了美元 —— 那样定价卡的「订阅」会把他送回人民币
      // 那一轨。`plans.currencyCode` 就是这一页正在展示的那套价格。
      startCheckout({ cycle, currency: plans.currencyCode ?? undefined })
    })
  }

  /**
   * 反馈 6(1)（2026-10-06）：只有「还没领过试用」的访客才看到试用 CTA。
   *
   *   state === null         → 资格 payload 还在路上：先不渲染，
   *                            否则会闪一下又消失（也避免误导航，见 P2-1）
   *   !state.authenticated   → 匿名访客保留入口（点了走登录页，
   *                            2026-10-02 走查 T16 定的行为，不能一起删掉）
   *   state.eligible         → 可领取
   *
   * 只有「已登录 + 不可领取」（已领过 / 已付款 / 订阅已绑卡）才隐藏。
   *
   * ⚠️ `claimedHere` 是**本地覆盖**：领取成功后 refresh() 拉回来的 payload
   *    可能还是 eligible=true —— 插件的资格接口有 5 分钟缓存，而 provider 的
   *    refresh() 不带 `?refresh=1`。不覆盖的话，刚领完的用户还会看到一个
   *   可点的「获取7天免费试用」（再点一次只会拿到后端拒绝）。
   *    modal.tsx 用 claimedEndsAt 本地覆盖 phase 是同一个原因。
   */
  const [claimedHere, setClaimedHere] = useState(false)
  const showTrialCta =
    !claimedHere && state !== null && (!state.authenticated || state.eligible)

  /**
   * 首页试用入口的直接领取（2026-10-06 用户反馈 6）。
   *
   * 反馈原文：
   *   (1) 仅用户未领取过试用才出现获取7天免费试用按钮
   *   (2) 如果用户没有领取过试用 点击按钮,判断用户是否进入了指定Discord服务器,
   *       如果没进入就弹窗让他进入 如果进入了就发放试用
   *       (注意结果要用通知组件告知用户)
   *
   * 所以这个入口不再无条件开弹窗：**先判 phase 与 gate，再决定动作**。
   *
   * ⚠️ Discord 门槛必须在**点按钮之前**用客户端状态判 —— 不能靠 claim 的返回
   *    文案猜原因：`claimTrial()` 的 message 是后端原始 Error.message
   *    （lib/data/trial.ts 的 messageOf），而 "discord_required" 是
   *    trial-phase.ts 里由 state.discord.status 派生的**客户端**类型，
   *    从不出现在 claim 接口的返回契约里。
   *
   * 服务端仍是权威：claimTrial() 内部还有一次门槛校验。客户端这一判只是为了
   * 少发一次注定被拒的请求，并给用户正确的引导（去入服 / 重新授权）。
   *
   * B0-2 合规：自动扣费披露的**主位**是持久可见的试用横幅（Task 9 Step 1b）。
   * 但横幅受 localStorage 的 trial-banner-dismissed 与 cookie consent 门控，
   * 首页这条路径不保证它可见（Task 9 Step 1c 的前置条件判定为「可能看不到」），
   * 所以成功通知里补回同一句披露 —— 按计划这一步不能省。
   */
  const claimDirect = async () => {
    // 首页这条路径没有「刚领取」的中间态，claimedEndsAt 传 null。
    // ⚠️ 两个参数都是必填：签名是 (state, { claimedEndsAt, now })。
    const phase = deriveTrialPhase(state, {
      claimedEndsAt: null,
      now: Date.now(),
    })
    const gate = trialGateBlock(state)
    if (gate !== null && phase === "claim") {
      // 未入服 / 需重授权 → 开弹窗，它渲染对应的门槛分支（入服 CTA / 验证按钮）。
      openTrial()
      return
    }

    setClaimBusy(true)
    const result = await claimTrial().catch(() => ({
      status: "error" as const,
      message: "trial_claim_failed",
    }))
    setClaimBusy(false)

    if (result.status === "ok") {
      // 入口立刻收起来（不依赖那个可能有 5 分钟缓存的 refresh()）。
      setClaimedHere(true)
      // 刚领完试用 = 还没绑卡，此刻不会扣款，所以成功提示只说不扣款，
      // 不带价格（带价格等于暗示到期会收钱）。
      const disclosure = tTrialBanner("noChargeDisclosure")
      toast.success(t("trialClaimed", { days: state?.offer?.days ?? 7 }), {
        description: disclosure,
      })
      // 重读资格（按钮按设计就此消失），并让服务端组件跟着刷新。
      refresh()
      router.refresh()
      return
    }

    // 走到这里说明客户端没判出门槛但服务端拒了（状态过期 / 引擎侧另有校验）。
    // **不要**再按 message 猜原因。打日志留证据，给用户通用文案。
    console.warn("[trial-claim] backend refused:", result.message)
    toast.error(t("trialClaimFailed"))
  }

  if (!monthly && !yearlyPlan) {
    return (
      <section id="pricing" className="scroll-mt-20">
        <div className="container mx-auto px-4 py-24 md:px-6 2xl:max-w-[1400px]">
          <div className="mx-auto mb-10 max-w-2xl text-center lg:mb-14">
            <h2 className="text-3xl font-semibold tracking-tight">
              {cards.title}
            </h2>
          </div>
          <p
            className="text-muted-foreground text-center"
            data-testid="pricing-unavailable"
          >
            {t("unavailable")}
          </p>
        </div>
      </section>
    )
  }

  return (
    <section id="pricing" className="scroll-mt-20">
      <div className="container mx-auto px-4 py-24 md:px-6 2xl:max-w-[1400px]">
        {/* Title */}
        <div className="mx-auto mb-10 max-w-2xl text-center lg:mb-14">
          <h2 className="text-3xl font-semibold tracking-tight">
            {cards.title}
          </h2>
          <p className="mt-2 text-muted-foreground">{cards.description}</p>
        </div>
        {/* End Title */}
        {/* Switch: the cycle selected here drives every PRO CTA below */}
        <div className="flex items-center justify-center">
          <Label
            htmlFor="payment-schedule"
            className={cn("me-3", !yearly && "text-foreground")}
          >
            {cards.pro.monthlyName}
          </Label>
          <Switch
            id="payment-schedule"
            checked={yearly}
            onCheckedChange={setYearly}
            aria-label={t("billingTerm")}
          />
          <Label
            htmlFor="payment-schedule"
            className={cn("ms-3", yearly && "text-foreground")}
          >
            {cards.pro.yearlyName}
          </Label>
          {savePercent > 0 ? (
            <Badge variant="secondary" className="ms-4">
              {t("saveBadge", { percent: savePercent })}
            </Badge>
          ) : null}
        </div>
        {/* End Switch */}
        {/* Grid */}
        <div className="mx-auto mt-12 grid max-w-3xl gap-6 lg:grid-cols-2 lg:items-stretch">
          {/* FREE */}
          <Card className="flex flex-col">
            <CardHeader className="text-center">
              <CardTitle>{cards.free.name}</CardTitle>
              <CardDescription>{cards.free.description}</CardDescription>
            </CardHeader>
            <CardContent className="flex-1">
              <div className="text-center">
                <span className="text-5xl font-bold" data-testid="free-price">
                  {currencyCode ? formatMinor(0, currencyCode) : "$0.00"}
                </span>
                {/* 原价(反馈 #5):横线穿过文本 */}
                <span className="text-muted-foreground mt-1 block text-sm">
                  <s className="line-through">
                    {currencyCode === "usd" ? "$9.9" : "¥69.9"}
                  </s>
                </span>
                <span className="text-muted-foreground mt-1 block text-sm">
                  {cards.free.period}
                </span>
              </div>
              <ul className="mt-7 space-y-2.5 text-sm">
                {freeFeatures.map((feature) => (
                  <li key={feature} className="flex space-x-2">
                    <Check className="mt-0.5 h-4 w-4 shrink-0" />
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                asChild
                variant="outline"
                className="w-full"
                data-testid="free-plan-cta"
              >
                {DOWNLOAD_FREE_URL ? (
                  <a
                    href={DOWNLOAD_FREE_URL}
                    className="inline-flex items-center gap-x-2"
                  >
                    <DownloadIcon size={16} />
                    {cards.free.cta}
                  </a>
                ) : (
                  <Link
                    href="/#pricing"
                    className="inline-flex items-center gap-x-2"
                  >
                    <DownloadIcon size={16} />
                    {cards.free.cta}
                  </Link>
                )}
              </Button>
            </CardFooter>
          </Card>
          {/* End Card */}
          {/* PRO — highlighted + glare hover */}
          <GlareHover
            className="w-full rounded-xl"
            background="transparent"
            opacity={0.35}
            duration={900}
          >
            <Card className="relative flex h-full w-full flex-col border-primary">
              <CardHeader className="text-center">
                <CardTitle>{cards.pro.title}</CardTitle>
                <CardDescription>{cards.pro.description}</CardDescription>
              </CardHeader>
              <CardContent className="flex-1">
                <div className="text-center">
                  <span className="text-5xl font-bold" data-testid="pro-price">
                    {price
                      ? formatMinor(price.amount, price.currencyCode)
                      : "—"}
                  </span>
                  <span className="text-muted-foreground mt-1 block text-sm">
                    / {yearly ? t("pro.perYear") : t("pro.perMonth")}
                  </span>
                  {/* 原价(反馈 #5):横线穿过文本,仅月付(年付原价未给) */}
                  {!yearly ? (
                    <span className="text-muted-foreground mt-1 block text-sm">
                      <s className="line-through">
                        {price?.currencyCode === "usd" ? "$19.9" : "¥139.9"}
                      </s>
                    </span>
                  ) : null}
                  {yearly && yearlyPlan && monthly && savePercent > 0 ? (
                    <span className="text-muted-foreground mt-2 block text-xs">
                      {t("pro.equivalent", {
                        amount: formatMinor(
                          // 2026-10-25：这里曾经是 `Math.round(yearlyPlan.amount / 12)`。
                          // `amount` 是**元**（99.9），`Math.round` 把它抹成 8，于是
                          // 「约合每月」写成 US$8.00 而真实值是 8.325（应显示 8.33）。
                          // 除法交给 Intl 的小数位处理，不要自己取整。
                          yearlyPlan.amount / 12,
                          yearlyPlan.currencyCode
                        ),
                        percent: savePercent,
                      })}
                    </span>
                  ) : null}
                </div>
                <ul className="mt-7 space-y-2.5 text-sm">
                  {proFeatures.map((feature) => (
                    <li key={feature} className="flex space-x-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0" />
                      <span className="text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
              <CardFooter className="flex-col gap-y-2">
                {/* 2026-10-06 反馈 6: the trial CTA no longer opens the modal
                    unconditionally - it claims directly (server action) and
                    only falls back to the modal when the Discord gate blocks
                    the claim. 2026-10-02 walkthrough (T16): an anonymous
                    visitor goes straight to the sign-in page instead - the
                    modal can only offer a sign-in link. The CTA itself is
                    rendered only while a claim is actually available
                    (feedback 6(1)). */}
                {showTrialCta ? (
                  <Button
                    type="button"
                    className="w-full"
                    disabled={claimBusy}
                    onClick={() => {
                      // Only an explicitly anonymous visitor goes to sign-in; a
                      // null state is the initial load window, where navigating
                      // would eject a signed-in user (review P2-1).
                      if (state && !state.authenticated) {
                        router.push({
                          pathname: "/account",
                          query: { next: pathname },
                        })
                        return
                      }
                      void claimDirect()
                    }}
                    data-testid="pro-plan-cta-trial"
                  >
                    <span className="inline-flex items-center gap-x-2">
                      <HandCoinsIcon size={16} />
                      {t("pro.ctaTrial")}
                    </span>
                  </Button>
                ) : null}
                {/* BorderBeam 按钮(反馈:黑底、光束沿边缘流动) */}
                <BorderBeamButton
                  type="button"
                  disabled={pending || !purchaseGate.canPurchase}
                  onClick={subscribe}
                  title={
                    purchaseGate.canPurchase
                      ? undefined
                      : t(`gate.${purchaseGate.reason}`)
                  }
                  className="w-full bg-black text-white hover:bg-black/85 disabled:opacity-100 disabled:text-white"
                  brightness={1.8}
                  borderBeamClassName="w-full"
                  data-testid="pro-plan-cta"
                >
                  <span className="inline-flex items-center justify-center gap-x-2 whitespace-pre-wrap text-center">
                    {/* 一直动的图标(反馈 #2):CSS 循环 wiggle */}
                    <CreditCardIcon
                      size={16}
                      className="animate-icon-wiggle shrink-0"
                    />
                    {t("pro.ctaSubscribe")}
                  </span>
                </BorderBeamButton>
              </CardFooter>
            </Card>
          </GlareHover>
          {/* End Card */}
        </div>
        {/* End Grid */}
        {/* Comparison table — 90%-opaque panel so the grid background does
            not wash the rows out (user feedback 2026-09-28). */}
        <div className="bg-background/90 mt-20 rounded-2xl p-6 lg:mt-32 lg:p-10">
          <div className="mb-10 text-center">
            <h3 className="text-2xl font-semibold">{comparisonTable.title}</h3>
            <p className="text-muted-foreground mt-1">
              {comparisonTable.subtitle}
            </p>
          </div>
          {/* Two plans only — one table serves every breakpoint. The two
              PRO SKUs (monthly/yearly) lead the body with realtime prices. */}
          {/* Narrower than the panel (feedback #2): three columns of short
              labels do not need the full 1400px container. */}
          <div className="mx-auto max-w-3xl">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted hover:bg-muted">
                  <TableHead className="w-6/12 text-primary">
                    {t("comparison.feature")}
                  </TableHead>
                  <TableHead className="w-3/12 text-center text-primary">
                    {t("comparison.freeColumn")}
                  </TableHead>
                  <TableHead className="w-3/12 text-center text-primary">
                    {t("comparison.paidColumn")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="bg-muted/50">
                  <TableCell colSpan={3} className="font-bold">
                    {cards.title}
                  </TableCell>
                </TableRow>
                <TableRow className="text-muted-foreground">
                  <TableCell>{cards.pro.monthlyName}</TableCell>
                  <TableCell className="text-center">
                    <div className="mx-auto w-min">—</div>
                  </TableCell>
                  <TableCell className="text-center">
                    {monthly
                      ? formatMinor(monthly.amount, monthly.currencyCode)
                      : "—"}
                  </TableCell>
                </TableRow>
                <TableRow className="text-muted-foreground">
                  <TableCell>{cards.pro.yearlyName}</TableCell>
                  <TableCell className="text-center">
                    <div className="mx-auto w-min">—</div>
                  </TableCell>
                  <TableCell className="text-center">
                    {yearlyPlan
                      ? formatMinor(yearlyPlan.amount, yearlyPlan.currencyCode)
                      : "—"}
                  </TableCell>
                </TableRow>
                {comparisonGroups.map((group) => (
                  <React.Fragment key={group.label}>
                    <TableRow className="bg-muted/50">
                      <TableCell colSpan={3} className="font-bold">
                        {group.label}
                      </TableCell>
                    </TableRow>
                    {group.rows.map((feature) => (
                      <TableRow
                        key={feature.label}
                        className="text-muted-foreground"
                      >
                        <TableCell>{feature.label}</TableCell>
                        <TableCell>
                          <div className="mx-auto w-min">
                            {feature.free ? (
                              <Check
                                className="h-5 w-5"
                                aria-label={t("comparison.included")}
                              />
                            ) : (
                              <Minus
                                className="h-5 w-5"
                                aria-label={t("comparison.notIncluded")}
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="mx-auto w-min">
                            {feature.paid ? (
                              <Check
                                className="h-5 w-5"
                                aria-label={t("comparison.included")}
                              />
                            ) : (
                              <Minus
                                className="h-5 w-5"
                                aria-label={t("comparison.notIncluded")}
                              />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        {/* End Comparison table */}
      </div>
    </section>
  )
}
